import dayjs, { Dayjs } from 'dayjs'
import { v4 as uuid } from 'uuid'
import { BrandPlanSchedule, BrandPlanTactic, BrandPlanTacticContents, BrandPlanTopics } from 'types'
import { ContentType, Schedule } from 'hooks/queries/schedules/useAddToBrandPlanSchedule'
import { ContentCategory, CONTENT_CATEGORY_CONFIG, CONTENT_CATEGORIES, TopicRow, PostType } from './Types'
import { ContentTypeCounts } from 'utils/brandPlans/contentCategories'
import { platformManualSchedulingMapping } from '../../Types'
import {
  EMAIL_TACTICS,
  SEO_TACTICS,
  SOCIAL_MEDIA_TACTICS,
  STORY_TACTICS,
  VIDEO_TACTICS as BRAND_PLAN_VIDEO_TACTICS,
} from 'components/FirstTimeExperienceModal/steps/BrandPlanTactics/BrandPlanTactics'

const TOPIC_FIELDS_BY_POST_TYPE: Record<PostType, readonly string[]> = {
  social_post: [
    'crosspost_topics',
    'instagram_topics',
    'facebook_topics',
    'linkedin_topics',
    'twitter_topics',
    'google_my_business_topics',
  ],
  video: ['video_crosspost_topics', 'tiktok_topics', 'youtube_topics'],
  blog_post: ['seo_topics'],
  email: ['email_topics'],
}

const shuffle = <T>(arr: T[]): T[] => {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

const collectTopicsFromFields = (source: Record<string, unknown>, fields: readonly string[]): string[] => {
  const allTopics: string[] = []
  for (const field of fields) {
    const topics = source[field]
    if (Array.isArray(topics)) {
      allTopics.push(...(topics as string[]).filter(Boolean))
    }
  }
  return [...new Set(allTopics)]
}

export const sampleTopicForPostType = (
  preview: Partial<BrandPlanTopics>,
  postType: PostType,
  exclude: Set<string>,
): string => {
  const fields = TOPIC_FIELDS_BY_POST_TYPE[postType]
  const available = shuffle(
    collectTopicsFromFields(preview as Record<string, unknown>, fields).filter((t) => !exclude.has(t)),
  )
  return available[0] || ''
}

const sumContents = (contents: BrandPlanTacticContents | undefined | null): number => {
  if (!contents || !Array.isArray(contents)) return 0
  return contents.reduce((sum, item) => sum + (item.count || 0), 0)
}

export const getPostTypeDistribution = (schedule: BrandPlanSchedule, totalCount: number): PostType[] => {
  const pattern: PostType[] = [
    ...Array<PostType>(sumContents(schedule.crosspost_contents)).fill('social_post'),
    ...Array<PostType>(sumContents(schedule.video_crosspost_contents)).fill('video'),
    ...Array<PostType>(sumContents(schedule.seo_contents)).fill('blog_post'),
    ...Array<PostType>(sumContents(schedule.email_contents)).fill('email'),
  ]

  if (pattern.length === 0) {
    return Array(totalCount).fill(getDefaultPostType(schedule))
  }

  return Array.from({ length: totalCount }, (_, i) => pattern[i % pattern.length])
}

export const getNextPostType = (schedule: BrandPlanSchedule, currentRowCount: number): PostType => {
  return getPostTypeDistribution(schedule, currentRowCount + 1)[currentRowCount]
}

const SOCIAL_TACTICS = [
  BrandPlanTactic.Instagram,
  BrandPlanTactic.Facebook,
  BrandPlanTactic.LinkedIn,
  BrandPlanTactic.Twitter,
]
const VIDEO_TACTICS = [BrandPlanTactic.TikTok, BrandPlanTactic.YouTube, BrandPlanTactic.InstagramReel]

export const getDefaultPostType = (schedule: BrandPlanSchedule): PostType => {
  const tactics = schedule.tactics || []
  if (tactics.some((t) => SOCIAL_TACTICS.includes(t))) return 'social_post'
  if (tactics.some((t) => VIDEO_TACTICS.includes(t))) return 'video'
  if (tactics.includes(BrandPlanTactic.SEO)) return 'blog_post'
  if (tactics.includes(BrandPlanTactic.Email)) return 'email'
  return 'social_post'
}

export const getDefaultTacticsForPostType = (
  postType: PostType,
  scheduleTactics: BrandPlanTactic[],
): BrandPlanTactic[] => {
  let filtered: BrandPlanTactic[]
  let allOptions: BrandPlanTactic[]

  switch (postType) {
    case 'social_post':
      filtered = scheduleTactics.filter((t) => SOCIAL_TACTICS.includes(t))
      allOptions = SOCIAL_TACTICS
      break
    case 'video':
      filtered = scheduleTactics.filter((t) => VIDEO_TACTICS.includes(t))
      allOptions = VIDEO_TACTICS
      break
    case 'blog_post':
      filtered = scheduleTactics.filter((t) => t === BrandPlanTactic.SEO)
      allOptions = [BrandPlanTactic.SEO]
      break
    case 'email':
      filtered = scheduleTactics.filter((t) => t === BrandPlanTactic.Email)
      allOptions = [BrandPlanTactic.Email]
      break
    default:
      return []
  }

  return filtered.length > 0 ? filtered : allOptions
}

/** Preference order when seeding multiple Gen V2 posts (e.g. PostSaveModal). */
const GEN_V2_CONTENT_CATEGORY_ORDER: ContentCategory[] = [
  'still_image_post',
  'carousel_post',
  'feed_video_post',
  'short_form_video',
  'stories',
  'blog_post',
  'newsletter',
]

const tacticsIntersect = (scheduleTactics: BrandPlanTactic[], pool: BrandPlanTactic[]) => {
  const set = new Set(scheduleTactics)
  return pool.some((t) => set.has(t))
}

/**
 * Content categories the schedule’s tactics can produce (for quick-add / batch defaults).
 */
export const getSupportedContentCategoriesForTactics = (scheduleTactics: BrandPlanTactic[]): ContentCategory[] => {
  const hasSocial = tacticsIntersect(scheduleTactics, SOCIAL_MEDIA_TACTICS)
  const hasVideo = tacticsIntersect(scheduleTactics, BRAND_PLAN_VIDEO_TACTICS)
  const hasStory = tacticsIntersect(scheduleTactics, STORY_TACTICS)
  const hasSeo = tacticsIntersect(scheduleTactics, SEO_TACTICS)
  const hasEmail = tacticsIntersect(scheduleTactics, EMAIL_TACTICS)

  return GEN_V2_CONTENT_CATEGORY_ORDER.filter((category) => {
    switch (category) {
      case 'still_image_post':
      case 'carousel_post':
      case 'feed_video_post':
        return hasSocial
      case 'short_form_video':
        return hasVideo
      case 'stories':
        return hasStory
      case 'blog_post':
        return hasSeo
      case 'newsletter':
        return hasEmail
      default:
        return false
    }
  })
}

/** Fallback when tactics yield no categories (e.g. schedule not loaded). */
const GEN_V2_QUICK_ADD_FALLBACK_CATEGORIES: ContentCategory[] = [
  'still_image_post',
  'carousel_post',
  'feed_video_post',
  'short_form_video',
]

export const GEN_V2_QUICK_ADD_POST_COUNT = 4

/** Categories for the Gen V2 “generate 4 posts” quick-add (PostSaveModal, etc.). */
export const getGenV2QuickAddCategories = (
  scheduleTactics: BrandPlanTactic[],
  postCount: number,
): ContentCategory[] => {
  const supported = getSupportedContentCategoriesForTactics(scheduleTactics)
  const pool = supported.length > 0 ? supported : GEN_V2_QUICK_ADD_FALLBACK_CATEGORIES
  return Array.from({ length: postCount }, (_, i) => pool[i % pool.length])
}

export type GenV2QuickAddCreditCounts = {
  blogCount: number
  emailCount: number
  shortFormVideoCount: number
  stillImageCount: number
  feedVideoCount: number
  carouselCount: number
  storyCount: number
}

/** Maps selected categories to {@link useCreditsToGenerateContent} per-type counts. */
export const countContentCategoriesForCredits = (categories: ContentCategory[]): GenV2QuickAddCreditCounts => {
  const counts: GenV2QuickAddCreditCounts = {
    blogCount: 0,
    emailCount: 0,
    shortFormVideoCount: 0,
    stillImageCount: 0,
    feedVideoCount: 0,
    carouselCount: 0,
    storyCount: 0,
  }
  for (const c of categories) {
    switch (c) {
      case 'still_image_post':
        counts.stillImageCount++
        break
      case 'carousel_post':
        counts.carouselCount++
        break
      case 'feed_video_post':
        counts.feedVideoCount++
        break
      case 'short_form_video':
        counts.shortFormVideoCount++
        break
      case 'stories':
        counts.storyCount++
        break
      case 'blog_post':
        counts.blogCount++
        break
      case 'newsletter':
        counts.emailCount++
        break
      default:
        break
    }
  }
  return counts
}

export const POST_TYPE_TO_CONTENT_TYPE: Record<PostType, ContentType> = {
  social_post: 'crosspost_post',
  video: 'video_crosspost_post',
  blog_post: 'blog_post',
  email: 'newsletter',
}

export const computePostTimes = (schedule: BrandPlanSchedule, count: number): Dayjs[] => {
  const startDate = dayjs(schedule.start_date)
  const endDate = dayjs(schedule.end_date)
  const today = dayjs()
  const tomorrow = today.add(1, 'day')
  const minDate = startDate.isAfter(tomorrow) ? startDate : tomorrow
  const effectiveStart = minDate.isAfter(endDate) ? endDate : minDate
  const daysDiff = endDate.diff(effectiveStart, 'day')

  const weekDays = schedule.week_days || []
  const weekDaySet = new Set(weekDays.map((d: string) => d.toLowerCase()))

  const validDays: Dayjs[] = []
  for (let i = 0; i <= daysDiff; i++) {
    const candidate = effectiveStart.add(i, 'day')
    const dayName = candidate.format('dddd').toLowerCase()
    if (weekDaySet.size === 0 || weekDaySet.has(dayName)) {
      validDays.push(candidate)
    }
  }

  if (validDays.length === 0) {
    validDays.push(effectiveStart)
  }

  const times: Dayjs[] = []
  for (let i = 0; i < count; i++) {
    const day = validDays[i % validDays.length]
    const isWeekend = [0, 6].includes(day.day())
    const bestTimes = platformManualSchedulingMapping['crosspost_post']?.best_times_of_day_to_post
    const window = isWeekend ? bestTimes?.weekends : bestTimes?.weekdays
    const fromHour = window?.from ?? 11
    const toHour = window?.to ?? 15
    const midMinutes = Math.round(((toHour - fromHour) * 60) / 2)
    const hour = fromHour + Math.floor(midMinutes / 60)
    const minute = midMinutes % 60
    times.push(day.hour(hour).minute(minute).second(0).millisecond(0))
  }

  return times
}

export const buildInitialTopicRowsFromCounts = (schedule: BrandPlanSchedule, counts: ContentTypeCounts): TopicRow[] => {
  const scheduleTactics = schedule.tactics || []
  const total = CONTENT_CATEGORIES.reduce((sum, category) => sum + counts[category], 0)
  if (total === 0) return []

  const postTimes = computePostTimes(schedule, total)
  const rows: TopicRow[] = []
  let timeIndex = 0

  for (const category of CONTENT_CATEGORIES) {
    const n = counts[category]
    if (n <= 0) continue
    const config = CONTENT_CATEGORY_CONFIG[category]
    for (let i = 0; i < n; i++) {
      rows.push({
        id: uuid(),
        topic: '',
        postType: config.postType,
        contentCategory: category,
        tactics: getDefaultTacticsForPostType(config.postType, scheduleTactics),
        postAt: postTimes[timeIndex++],
        loading: true,
        userEdited: false,
      })
    }
  }

  return rows
}

export const buildInitialTopicRows = (
  schedule: BrandPlanSchedule,
  postCount: number,
  contentCategory?: ContentCategory,
): TopicRow[] => {
  const scheduleTactics = schedule.tactics || []
  const postTimes = computePostTimes(schedule, postCount)

  if (contentCategory) {
    const config = CONTENT_CATEGORY_CONFIG[contentCategory]
    return Array.from({ length: postCount }, (_, i) => ({
      id: uuid(),
      topic: '',
      postType: config.postType,
      contentCategory,
      tactics: getDefaultTacticsForPostType(config.postType, scheduleTactics),
      postAt: postTimes[i],
      loading: true,
      userEdited: false,
    }))
  }

  const postTypes = getPostTypeDistribution(schedule, postCount)
  return postTypes.map((postType, i) => ({
    id: uuid(),
    topic: '',
    postType,
    tactics: getDefaultTacticsForPostType(postType, scheduleTactics),
    postAt: postTimes[i],
    loading: true,
    userEdited: false,
  }))
}

/** Story API `type`: Facebook-only story tactics must not use `instagram_story`. */
export const storyApiTypeFromTactics = (tactics: BrandPlanTactic[] | undefined): ContentType => {
  const set = new Set(tactics ?? [])
  if (set.has(BrandPlanTactic.Facebook) && !set.has(BrandPlanTactic.Instagram)) {
    return 'facebook_story'
  }
  return 'instagram_story'
}

export const expandTopicRowsToPayload = (topicRows: TopicRow[]): Schedule[] => {
  return topicRows.map((row) => {
    if (row.contentCategory) {
      const config = CONTENT_CATEGORY_CONFIG[row.contentCategory]
      const type: ContentType =
        row.contentCategory === 'stories' ? storyApiTypeFromTactics(row.tactics) : (config.apiType as ContentType)
      return {
        type,
        topic: row.topic,
        post_at: row.postAt,
        initial_media_file_ids: row.mediaFileIds?.map(String),
        ...((row.postType === 'social_post' || row.postType === 'video') &&
          row.tactics?.length && { tactics: row.tactics }),
        ...(config.contentType && { content_type: config.contentType }),
      }
    }

    return {
      type: POST_TYPE_TO_CONTENT_TYPE[row.postType],
      topic: row.topic,
      post_at: row.postAt,
      initial_media_file_ids: row.mediaFileIds?.map(String),
      ...((row.postType === 'social_post' || row.postType === 'video') &&
        row.tactics?.length && { tactics: row.tactics }),
    }
  })
}

export const POST_NAME_LABEL: Record<ContentCategory, string> = {
  still_image_post: 'post',
  carousel_post: 'post',
  feed_video_post: 'post',
  short_form_video: 'post',
  stories: 'story',
  newsletter: 'email',
  blog_post: 'blog',
}
